import classnames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
// tslint:disable-next-line: no-submodule-imports
import { IoExitOutline } from 'react-icons/io5';

import { DbKey } from '../../../../adl-gen/common/db';
import { AppClient, ApplicationRole } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { i18next } from '../../../../internationalization/i18next';
import { PrinterIcon } from '../../../../ui/common/icons';
import { MenuLink, PageLayoutParams } from '../../../../ui/common/page-layout/page-layout';
import { SelectOption } from '../../../../ui/widgets/select/select';
import { SideNav } from '../../components/sidebar/side-nav';

const styles = require('../../../../ui/common/page-layout/page-layout.css');

// /** An abstracted layout function */
export type AdminPageLayoutFn = (params: AdminPageLayoutParams) => JSX.Element;

export interface AdminPageLayoutParams extends PageLayoutParams {
  headerLinks?: MenuLink[];
  subLinks?: MenuLink[];
  /** Link items to be shown in the sidebar */
  sidebarLinks?: MenuLink[];
  sidebarSubMenus?: { [parentLabel: string]: MenuLink[] };

  /* Client options for selection by superuser (or just the one for client admin) */
  appClientOptions?: SelectOption[];

  /* If client already selected set as defaultValue */
  selectedClient?: DbKey<AppClient>;

  /* Navigation action for when a Client ID is selected */
  onSelectClient?(appClientId: DbKey<AppClient>): void;

  /** Elements of the primary content area */
  children?: React.ReactNode;
}

/** Props for the component */
export interface AdminPageLayoutProps extends AdminPageLayoutParams {
  logoUrl: string;
  role: ApplicationRole;
  userFullName?: string;
  logout?(): void;
}

/**
 * Component implementing typical pages
 *
 * Pick various styles as needed to render the Admin UI differently from the main app
 */
@observer
export class AdminPageLayout extends React.Component<AdminPageLayoutProps> {
  @observable sidebarCollapsed: boolean = false;

  @action
  toggleSidebarCollapse = () => {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  };

  onSelectClient = (appClientId: DbKey<AppClient>): void => {
    if (this.props.onSelectClient) {
      return this.props.onSelectClient(appClientId);
    }
  };

  /** Renders the header */
  renderHeader() {
    const headerLinks = this.props.headerLinks || [];
    const links = headerLinks.map(hl => (
      <div
        className={classnames(styles.headerLink, hl.isHighlighted ? styles.selected : undefined)}
        onClick={hl.action}
        key={hl.label}
        data-tour={hl.label}
      >
        {hl.label}
      </div>
    ));
    return (
      <header className={styles.header}>
        <div className={classnames(styles.headerRow, styles.default)}>
          <div className={styles.headerLeft}>
            <img className={styles.logo} src={this.props.logoUrl} />
            {links}
          </div>
          <div className={styles.headerRight}>
            {/* {this.props.langSelector} */}
            {this.props.userFullName}
            {this.props.logout && (
              <div className={styles.logout} onClick={this.props.logout}>
                <IoExitOutline size={20} title={i18next.t('header.logout', 'Logout')} />
                {/* {i18next.t("header.logout", "(Logout)")} */}
              </div>
            )}
          </div>
        </div>
        {this.props.title ? this.renderTitleAndSubnav() : undefined}
      </header>
    );
  }

  /// This routine controls whether Print/Roles Filter/People Filter display at all by testing for the presence of
  /// their respective callbacks.
  renderTitleAndSubnav() {
    return (
      <div className={classnames(styles.headerRow)}>
        <div className={classnames(styles.headerLeft, styles.pageInfo)}>
          {this.props.title ? this.renderTitle() : undefined}
          {/* <div className={styles.subnav}> */}
          <nav>
            {/* Render subnav here */}
            {this.props.subLinks
              ? this.props.subLinks.map(link => this.renderSubNav(link.label, link.action, link.isDisabled))
              : undefined}
          </nav>
        </div>
        <div className={classnames(styles.headerRight, styles.actionButtons)}>
          {this.props.onClickPrint ? this.renderPrintButton() : undefined}
        </div>
      </div>
    );
  }

  renderSubNav(title: string, menuAction?: () => void, disabled?: boolean) {
    return (
      <div
        className={
          disabled
            ? classnames(styles.subnavLink, styles.disabled)
            : menuAction
            ? styles.subnavLink
            : classnames(styles.subnavLink, styles.selected)
        }
        onClick={menuAction}
        key={title}
        data-tour={title}
      >
        {title}
      </div>
    );
  }

  renderTitle() {
    return <div className={classnames(styles.headerTitle)}>{this.props.title}</div>;
  }

  /** Renders the content body */
  renderBody() {
    // const { layoutType: layout } = this.props;
    // const style = layout === "tabbedContentUi" ? styles.tabbedBody : styles.body;
    return <section className={styles.tabbed}>{this.props.children}</section>;
  }

  // renderBody() {
  //   // const { layoutType: layout } = this.props;
  //   // const style = layout === "tabbedContentUi" ? styles.tabbedBody : styles.body;
  //   // return <div className={style}>{this.props.children}</div>;
  //   return <section>{this.props.children}</section>;
  // }

  // TODO: Import Print icon from Rich
  renderPrintButton(): JSX.Element {
    return <PrinterIcon size={'xsmall'} onClick={this.props.onClickPrint} />;
  }

  renderFooter() {
    return (
      <footer>
        <ul>
          <li>
            <a href="https://www.skillseeker.io/wp-content/uploads/2021/04/Privacy-Notice.pdf">Privacy Notice</a>
          </li>
          <li>
            <a href="https://www.skillseeker.io/wp-content/uploads/2021/04/Terms-of-Use.pdf">Terms of Service</a>
          </li>
        </ul>
      </footer>
    );
  }

  /** Renders the page */
  render() {
    console.debug(
      'DEBUG AdminPageLayout render: appClientOptions',
      this.props.appClientOptions ? this.props.appClientOptions.map(opt => opt.label).join(',') : undefined
    );
    console.debug('DEBUG AdminPageLayout render: selectedClient', this.props.selectedClient);
    return (
      <div className={styles.pageContainer}>
        <div className={styles.adminPage}>
          {this.renderHeader()}
          <div className={styles.pageWithSidebar}>
            {/* <div className={styles.sidebar}> */}
            <aside className={styles.sidebar}>
              <SideNav
                collapsed={this.sidebarCollapsed}
                toggled={false}
                toggleSidebarCollapse={this.toggleSidebarCollapse}
                menuLinks={this.props.sidebarLinks || []}
                subMenuLinks={this.props.sidebarSubMenus || {}}
                role={this.props.role}
                appClientOptions={this.props.appClientOptions || []}
                selectedClient={this.props.selectedClient}
                onSelectClient={this.onSelectClient}
              />
            </aside>
            <div className={styles.main}>{this.renderBody()}</div>
          </div>
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/common/page-layout/admin-page-layout.tsx